import React, { useEffect, useState } from "react";
import { useTable, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import moment from "moment";
import Config, { PaginationLimit } from "../../Config";
import { capitalizeFirstLetter, truncateText } from "../../helpers";
import { IoMdEye } from "react-icons/io";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import Tooltip from "../Tooltip";
import { MdOutlineChevronRight } from "react-icons/md";

const Table = ({
    apiData = [],
    setSelectedData,
    setIsViewDetails,
}) => {
    const storageKey = `housekeeping_active_page`;
    const [data, setData] = useState([]);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [selectedRow, setSelectedRow] = useState(null);
    const storageValue = sessionStorage.getItem(storageKey);
    const savedPage = storageValue !== null ? Number(storageValue) : 0;
    const [currentPage, setCurrentPage] = useState(savedPage);

    useEffect(() => {
        setCurrentPage(savedPage);
    }, [savedPage]);

    const viewDetails = (item) => {
        setIsViewDetails(true);
        setSelectedData(item);
    };

    // ---------- Cell renderers ----------
    const getRoomCell = (item) => (
        <div className="flex flex-col gap-0.5">
            <span className="font-medium text-gray-800 text-sm">
                Room {item?.roomNum ?? "-"}
            </span>
            <div className="flex items-center">
                <span className="text-xs text-gray-400 mr-1">
                    {item?.propType === Config.PROPERTY_TYPE.PG ? "Floor" : "Flat"}-
                    {item?.flatFloorName ?? "-"},
                </span>
                <span className="text-xs text-gray-400 truncate" title={item?.propName}>
                    {capitalizeFirstLetter(truncateText(item?.propName, 25))}
                </span>
            </div>
        </div>
    );

    const getStatusCell = (item) => {
        const task = item?.tasks?.[0];
        const isClean = (task?.taskCompleted ?? 0) > 0;

        return isClean ? (
            <Cleaned>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    Cleaned
                </span>
            </Cleaned>
        ) : (
            <NotCleaned>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                    Not Cleaned
                </span>
            </NotCleaned>
        );
    };

    const getDoneByCell = (item) => {
        const task = item?.tasks?.[0];
        const isClean = (task?.taskCompleted ?? 0) > 0;

        if (!isClean) {
            return (
                <span className="text-xs font-medium text-gray-400">
                    Not assigned
                </span>
            );
        }

        return (
            <span className="text-sm text-gray-600">
                {task?.staffName
                    ? capitalizeFirstLetter(task.staffName)
                    : "—"}
            </span>
        );
    };

    const getTimeCell = (item) => {
        const task = item?.tasks?.[0];
        const isClean = (task?.taskCompleted ?? 0) > 0;

        if (!isClean) {
            return (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-600">
                    Pending
                </span>
            );
        }

        return (
            <span className="text-sm text-gray-600">
                {task?.taskDate
                    ? moment(task.taskDate).format("hh:mm A")
                    : "—"}
            </span>
        );
    };

    const ActionBtns = (item) => {
        const task = item?.tasks?.[0];
        const isClean = !!task;

        return (
            <Tooltip label={isClean ? "View details" : "No details available"}>
                <button
                    onClick={() => {
                        if (isClean) viewDetails(item);
                    }}
                    disabled={!isClean}
                    className={`transition text-gray-400 ${isClean
                        ? "hover:text-gray-700 cursor-pointer"
                        : "opacity-50 cursor-not-allowed"
                        }`}
                >
                    <MdOutlineChevronRight size={22} />
                </button>
            </Tooltip>
        );
    };
    // ---------- Map API → table rows ----------
    useEffect(() => {
        if (apiData) {
            const tempData = apiData.map((item) => ({
                room: getRoomCell(item),
                status: getStatusCell(item),
                doneBy: getDoneByCell(item),
                time: getTimeCell(item),
                action: ActionBtns(item),
            }));
            setData(tempData);
        }
    }, [apiData]);

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ---------- Columns ----------
    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                { Header: "Room", accessor: "room" },
                { Header: "Status", accessor: "status" },
                { Header: "", accessor: "action" },
            ];
        }
        return [
            { Header: "Room", accessor: "room" },
            { Header: "Status", accessor: "status" },
            { Header: "Done By", accessor: "doneBy" },
            { Header: "Time", accessor: "time" },
            { Header: "", accessor: "action" },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        usePagination,
    );

    useEffect(() => {
        sessionStorage.setItem(storageKey, pageIndex);
    }, [pageIndex, storageKey]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th
                                    {...column.getHeaderProps(

                                    )}
                                >
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc
                                                    ? "-rotate-90"
                                                    : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody
                    {...getTableBodyProps()}
                    hasdata={(apiData?.length > 0).toString()}
                >
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        const pageItem = apiData[pageIndex * PaginationLimit + rowIndex];
                        if (!pageItem) return null;
                        const rowId = String(pageItem.id);

                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({
                                        style: { height: "80px" },
                                    })}
                                    hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell) => (
                                        <Td {...cell.getCellProps()}>
                                            {cell.render("Cell")}
                                        </Td>
                                    ))}
                                </Tr>

                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>Done by</strong>{" "}
                                                    {data[rowIndex].doneBY}
                                                </li>
                                                <li>
                                                    <strong>Time</strong>{" "}
                                                    {data[rowIndex].time}
                                                </li>

                                                <li className="flex gap-2">
                                                    <strong>Action</strong>{" "}
                                                    {data[rowIndex].action}
                                                </li>

                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>

            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn
                            onClick={() => previousPage()}
                            disabled={!canPreviousPage}
                        >
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 opacity-75 "
                            />
                        </PrevBtn>
                        <NextBtn
                            onClick={() => nextPage()}
                            disabled={!canNextPage}
                        >
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const CustomTable = tw.table` w-full text-gray-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) =>
    props.hasdata === "true" ? "border-b" : ""}`;
const Tr = tw.tr`
  border-b
  hover:bg-slate-50
  transition
`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;

const Cleaned = tw.div`
text-green-500 text-sm font-semibold
`;
const NotCleaned = tw.div`
  text-rose-500 font-semibold text-sm 
`;

export default Table;